import { useEffect, useRef, useState } from 'react'
import { motionReduced } from '../../lib/usePrefersReducedMotion'
import styles from './Landing.module.css'

/**
 * THE HERO. The Writing Surface, rebuilt at landing-page scale and typing itself out.
 *
 * Kamrin's direction, 2026-09-23: *"make it look more like the actual app UI and then have text
 * typing in like someone is actively writing, when it makes a mistake, make it underline in
 * real-time to show it actively helping."* It replaced a static depiction of the chapter scale,
 * which was true but inert.
 *
 * ── Everything here is the real thing, and that is the point ─────────────────────────────────────
 * The chrome is the Writing Surface's own: a `--topbar` bar over a `--write-bg` page with its own
 * ink, the oversized chapter numeral cropped by the left margin at 5.5% opacity, prose in
 * `--f-reading` at `--read-line-height`, and the chapter scale along the bottom with its walked
 * segment and its one lit traveler. `WritingSurface.module.css` is the source for all of it.
 *
 * **The three flags are real flags.** `src/lib/proofing.ts` would raise every one of them on this
 * paragraph: `almost` is in `WEASEL_WORDS`, `felt` is the first of the `SHOW_TELL_PHRASES`, and
 * `her heart skipped a beat` is in `CLICHES` verbatim. The panel copy is that module's own
 * template — "“almost” — consider removing" over "Weasel word" — not a paraphrase of it. A test
 * asserts the paragraph still trips the real engine, so this cannot quietly become a mockup.
 *
 * ── One deliberate divergence from the app ───────────────────────────────────────────────────────
 * In the app, only the weasel check underlines; the other eleven paint a soft wash behind the text
 * (`global.css`, `.proofHl[data-check=...]`). Here all three underline, because that is what Kamrin
 * asked for and because a 2px rule reads at a glance where a 26%-alpha wash does not. The colours
 * are the real `--proof-*` tokens either way.
 *
 * ── Motion ───────────────────────────────────────────────────────────────────────────────────────
 * Reduced motion gets the finished paragraph, underlines and panel already in place — the swap, not
 * the strip. The run starts on an observer OR a timer, never the observer alone: a demo that never
 * starts is an empty page, which is the same failure shape §3's reveal rule exists to prevent.
 */

/** A run of the paragraph. `check` names the real `proofing.ts` check that would flag it. */
type Segment = { text: string; check?: 'weasel' | 'showtell' | 'cliche' }

const PARAGRAPH: Segment[] = [
  { text: 'The lighthouse had been dark for three winters, and the village had ' },
  { text: 'almost', check: 'weasel' },
  { text: ' stopped counting them. She ' },
  { text: 'felt', check: 'showtell' },
  { text: ' the cold come up through the boards, and ' },
  { text: 'her heart skipped a beat', check: 'cliche' },
  { text: '.' },
]

/** `proofing.ts`'s own issue copy, in its own order. Not a paraphrase. */
const ISSUES: { check: Segment['check']; text: string; sub: string }[] = [
  { check: 'weasel', text: '“almost”: consider removing', sub: 'Weasel word' },
  { check: 'showtell', text: '“felt”: possible telling', sub: 'Possible show vs tell' },
  { check: 'cliche', text: '“her heart skipped a beat”: cliché', sub: 'Cliché' },
]

const FULL = PARAGRAPH.reduce((n, s) => n + s.text.length, 0)
const MS_PER_CHAR = 26
/** Long enough to read as writing rather than a progress bar, short enough to finish on screen. */
const SETTLE_MS = 600

const CHAPTERS = 12
const CURRENT = 9

export default function WritingDemo() {
  const ref = useRef<HTMLDivElement>(null)
  // Start finished when motion is off, so the reduced-motion path renders the end state and never
  // needs the timer at all.
  const [typed, setTyped] = useState(() => (motionReduced() ? FULL : 0))
  const [done, setDone] = useState(() => motionReduced())

  useEffect(() => {
    const el = ref.current
    if (!el || motionReduced()) return

    let raf = 0
    let settle = 0
    let started = false
    const run = () => {
      if (started) return
      started = true
      const t0 = performance.now()
      const frame = (now: number) => {
        const n = Math.min(FULL, Math.floor((now - t0) / MS_PER_CHAR))
        setTyped(n)
        if (n < FULL) raf = requestAnimationFrame(frame)
        else settle = window.setTimeout(() => setDone(true), SETTLE_MS)
      }
      raf = requestAnimationFrame(frame)
    }

    let io: IntersectionObserver | null = null
    if ('IntersectionObserver' in window) {
      io = new IntersectionObserver(
        (entries) => {
          if (!entries[0]?.isIntersecting) return
          io?.disconnect()
          run()
        },
        { threshold: 0.35 },
      )
      io.observe(el)
    }
    // The backstop, and the reason the observer is optional rather than load-bearing.
    const backstop = window.setTimeout(run, 1200)

    return () => {
      cancelAnimationFrame(raf)
      window.clearTimeout(settle)
      window.clearTimeout(backstop)
      io?.disconnect()
    }
  }, [])

  // Slice the paragraph to however much has been typed. A flagged run only takes its underline
  // once it is fully typed — the app cannot flag a word it has not seen the end of either.
  let cursor = 0
  const rendered: React.ReactNode[] = []
  for (const [i, seg] of PARAGRAPH.entries()) {
    const start = cursor
    cursor += seg.text.length
    if (typed <= start) break
    const shown = seg.text.slice(0, Math.max(0, typed - start))
    const complete = typed >= cursor
    rendered.push(
      seg.check && complete ? (
        <span key={i} className={styles.proofHl} data-check={seg.check}>
          {shown}
        </span>
      ) : (
        <span key={i}>{shown}</span>
      ),
    )
  }

  return (
    <div className={styles.demo} ref={ref}>
      {/* The app's own chrome. Decorative as a whole: the hero's own heading and lede say what it
          shows, and a screen reader has no use for a re-enactment of a text editor. */}
      <div className={styles.demoWindow} aria-hidden="true">
        <div className={styles.demoBar}>
          <span className={styles.demoBack}>
            <svg viewBox="0 0 16 16" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
              <path d="M10 3 5 8l5 5" />
            </svg>
            Library
          </span>
          <span className={styles.demoTitle}>
            <b>The Lamplighter&rsquo;s Daughter</b>
            <i>Chapter nine &middot; The walk back</i>
          </span>
          <span className={styles.demoPill} data-on={done}>
            {done ? 'Saved' : 'Saving'}
          </span>
        </div>

        <div className={styles.demoPage}>
          <span className={styles.demoNumeral}>9</span>
          <p className={styles.demoProse}>
            {rendered}
            {!done && <span className={styles.caret} />}
          </p>

          <div className={styles.proofPanel} data-on={done}>
            <div className={styles.proofHead}>Proofing &middot; 3 to look at</div>
            {ISSUES.map((iss) => (
              <div key={iss.text} className={styles.proofRow}>
                <span className={styles.proofDot} data-check={iss.check} />
                <span>
                  <b>{iss.text}</b>
                  <i>{iss.sub}</i>
                </span>
              </div>
            ))}
          </div>
        </div>

        <div className={styles.scaleBox}>
          <div className={styles.scale}>
            <span className={styles.scaleLine} />
            <span
              className={styles.scaleWalked}
              style={{ width: `${((CURRENT - 1) / (CHAPTERS - 1)) * 100}%` }}
            />
            {Array.from({ length: CHAPTERS }, (_, i) => (
              <span
                key={i}
                className={`${styles.tick}${i === CURRENT - 1 ? ` ${styles.tickCurrent}` : ''}`}
                style={{ left: `${(i / (CHAPTERS - 1)) * 100}%` }}
              >
                <span className={styles.tickBar} />
                <span className={styles.tickNum}>{i + 1}</span>
              </span>
            ))}
            <span
              className={styles.traveler}
              style={{ left: `${((CURRENT - 1) / (CHAPTERS - 1)) * 100}%` }}
            />
          </div>
          <div className={styles.scaleCap}>
            Chapter <b>9</b> of 12 &middot; Beat: <b>All Is Lost</b> &middot; The lowest point.
          </div>
        </div>
      </div>
    </div>
  )
}
